// Die Massenpflege von Stichworten in der Oberflaeche (ST-TAX-002,
// Batch-Schnitt; ADR-0058).
//
// FACHSPRACHE: "Massenpflege", "Massenlauf", "Vorschau", "freigeben",
// "rückgängig machen" -- nie "Batch", nie eine Kennung (NFR-UX-003). Die
// Probe apps/web/test/classification-batches.test.ts haelt jeden Text gegen
// die Verbotsliste.
//
// DIE ZAHLEN sind Abschriften aus apps/api/src/governance/
// classification-batch-commands.ts, und die Probe liest sie dort: eine
// Abschrift ohne Gegenprobe laeuft auseinander, ohne dass es auffaellt.
//
// EIN SATZ JE GRUNDCODE mit Problem, Wirkung und naechstem Schritt
// (NFR-UX-005, Experience-Review 28.09.2026, P3). Wo etwas NICHT geschrieben
// wurde, sagt der Satz das ausdruecklich -- "nichts ist halb passiert".

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Hoechstzahl der Kontakte je Massenlauf (B1) -- Abschrift, Probe liest MAXIMUM_BATCH_PARTIES. */
export const MAXIMUM_BATCH_PARTIES = 250;

/** Ab so vielen wirksamen Aenderungen gibt eine zweite Person frei (B2) -- Abschrift von SECOND_PERSON_THRESHOLD. */
export const SECOND_PERSON_THRESHOLD = 100;

/** merge_terms haengt die Zuordnungen eines ersetzten Stichworts auf seinen Nachfolger um (ST-TAX-003 Schnitt 5). */
export type BatchOperation = 'assign' | 'remove' | 'merge_terms';
export type BatchStatus = 'previewed' | 'completed' | 'reversed' | 'discarded';
export type BatchItemOutcome = 'effective' | 'unchanged' | 'not_writable' | 'not_found';

export interface BatchCounts {
  readonly effectiveCount: number;
  readonly unchangedCount: number;
  readonly notWritableCount: number;
  readonly notFoundCount: number;
  readonly conflictCount: number;
}

export interface BatchSummary extends BatchCounts {
  readonly classificationChangeBatchId: string;
  readonly operation: BatchOperation;
  readonly status: BatchStatus;
  readonly taxonomyTermId: string;
  readonly termDisplayName: string;
  readonly isMine: boolean;
  readonly isReversal: boolean;
  readonly requiresSecondPerson: boolean;
  readonly createdAt: string;
  readonly previewExpiresAt: string;
  readonly previewCurrent: boolean;
  readonly completedAt: string | null;
  readonly reversibleUntil: string | null;
  readonly canReverse: boolean;
}

export interface BatchContact {
  readonly partyId: string;
  readonly displayName: string;
  readonly outcome: BatchItemOutcome | null;
}

export interface BatchDetail extends BatchSummary {
  readonly requestedCount: number;
  readonly outdated: boolean;
  readonly canExecute: boolean;
  readonly canDiscard: boolean;
  readonly contacts: readonly BatchContact[];
  readonly additionalContactCount: number;
  /** Nur bei merge_terms; alle Zahlen vom Server, keine im Client gerechnet. */
  readonly merge: BatchMergeFacts | null;
}

export interface BatchMergeFacts {
  readonly targetDisplayName: string;
  /** Merge: an so vielen Kontakten wird das Ziel neu vergeben. Umkehr: so viele Zeilen des Ziels enden. */
  readonly targetCount: number;
  /** Offene Zuordnungen ueber der Grenze von 250 -- sie gehen in einen Folgelauf. */
  readonly remainingCount: number;
}

export type ListResult =
  | { readonly outcome: 'loaded'; readonly ownBatches: readonly BatchSummary[]; readonly awaitingApproval: readonly BatchSummary[] }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type DetailResult =
  | { readonly outcome: 'loaded'; readonly batch: BatchDetail }
  | { readonly outcome: 'gone' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type CommandResult =
  | { readonly outcome: 'applied'; readonly classificationChangeBatchId: string; readonly dataQualityIssueCount: number }
  | { readonly outcome: 'rejected'; readonly reasonCode?: string; readonly message: PlainMessage };

// --- Die Saetze -------------------------------------------------------------

export const OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Seit der Vorschau hat sich etwas geändert.',
  explanation: 'Jemand hat an einem der Kontakte ein Stichwort vergeben oder entfernt. Es wurde nichts geändert.',
  nextStep: 'Leg die gleiche Auswahl erneut vor — die neue Vorschau zeigt den aktuellen Stand.',
  retryable: true,
});

export const EXPIRED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Vorschau ist älter als sieben Tage.',
  explanation: 'Eine Vorschau gilt höchstens eine Woche. Es wurde nichts geändert.',
  nextStep: 'Leg die gleiche Auswahl erneut vor.',
  retryable: true,
});

export const NOT_OPEN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Massenlauf ist schon abgeschlossen.',
  explanation: 'Er wurde ausgeführt, verworfen oder rückgängig gemacht — vielleicht von dir in einem anderen Fenster.',
  nextStep: 'Darunter siehst du, wie er jetzt steht.',
  retryable: false,
});

export const WITHOUT_EFFECT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Massenlauf würde nichts ändern.',
  explanation: 'Alle ausgewählten Kontakte haben das Stichwort schon bzw. haben es nicht.',
  nextStep: 'Verwirf den Lauf oder wähl andere Kontakte.',
  retryable: false,
});

export const SECOND_PERSON_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Massenlauf muss eine zweite Person freigeben.',
  explanation: `Ab ${SECOND_PERSON_THRESHOLD} Änderungen — zusammen mit deinen eigenen Läufen der letzten 24 Stunden — gibt eine andere Person frei. Es wurde nichts geändert.`,
  nextStep: 'Kopier den Link und schick ihn einer Kollegin oder einem Kollegen. Der Lauf steht bei ihr unter „Wartet auf deine Freigabe“.',
  retryable: false,
});

export const NOT_REVERSIBLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieser Massenlauf lässt sich nicht rückgängig machen.',
  explanation: 'Rückgängig machen geht nur bei einem ausgeführten Lauf, und nicht bei einer Rücknahme selbst.',
  nextStep: 'Wenn du etwas zurücknehmen möchtest, leg einen neuen Massenlauf in die Gegenrichtung an.',
  retryable: false,
});

export const REVERSAL_EXPIRED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Frist zum Rückgängigmachen ist vorbei.',
  explanation: 'Ein Massenlauf lässt sich 30 Tage lang rückgängig machen.',
  nextStep: 'Leg einen neuen Massenlauf in die Gegenrichtung an.',
  retryable: false,
});

export const TERM_NOT_ACTIVE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort ist nicht mehr vergebbar.',
  explanation: 'Es wurde zurückgezogen. Es wurde nichts geändert.',
  nextStep: 'Wähl ein anderes Stichwort aus der Liste.',
  retryable: false,
});

export const NOT_REPLACED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dieses Stichwort ist nicht ersetzt.',
  explanation: 'Zuordnungen lassen sich nur von einem Stichwort umhängen, das durch ein anderes ersetzt wurde. Es wurde nichts geändert.',
  nextStep: 'Ersetze das Stichwort zuerst im Stichwortkatalog oder wähl ein ersetztes.',
  retryable: false,
});

export const MERGE_TARGET_UNAVAILABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Nachfolger lässt sich gerade nicht vergeben.',
  explanation: 'Der Nachfolger ist selbst beendet, noch nicht freigegeben, noch nicht gültig oder für dich nicht sichtbar. Es wurde nichts geändert.',
  nextStep: 'Prüf den Nachfolger im Stichwortkatalog und versuch es danach erneut.',
  retryable: false,
});

export const GROUPWIDE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Gruppenweite Stichworte lassen sich hier nicht umhängen.',
  explanation: 'Sie gelten in der ganzen Gruppe und dürfen nur von der Datenpflege geändert werden. Diese Rolle gibt es in LIAISON noch nicht. Es wurde nichts geändert.',
  nextStep: 'Lass die Zuordnungen am bisherigen Stichwort.',
  retryable: false,
});

export const MERGE_WITHOUT_EFFECT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Es gibt nichts umzuhängen.',
  explanation: 'Kein Kontakt, den du sehen und ändern darfst, trägt dieses Stichwort noch. Es wurde nichts geändert.',
  nextStep: 'Du musst nichts weiter tun.',
  retryable: false,
});

export const GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Massenlauf gibt es für dich nicht.',
  explanation: 'Er gehört einer anderen Person und wartet nicht auf deine Freigabe, oder der Link ist unvollständig.',
  nextStep: 'Frag die Person, die dir den Link geschickt hat, oder öffne deine Massenläufe.',
  retryable: false,
});

export const SELECTION_GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Auswahl dieses Massenlaufs ist nicht mehr da.',
  explanation: 'LIAISON merkt sich eine Auswahl nur, solange du angemeldet bist und die Seite nicht neu lädst. Es wurde nichts geändert.',
  nextStep: 'Wähl die Kontakte in der Kontaktsuche erneut aus.',
  retryable: false,
});

/** Einen Massenlauf anlegen traegt keine Kennung: ob die Vorschau entstand, zeigt die Massenpflege. */
export const PREVIEW_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Vorschau angelegt wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Unter „Massenpflege“ siehst du deine Vorschauen; fehlt sie dort, lege sie neu an.',
  retryable: true,
});

/** Nur fuer Lesewege (Liste, Lauf, Stichworte): beim Lesen ist nichts passiert. */
export const UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Die Angaben ließen sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und lade die Seite neu.',
  retryable: true,
});

// --- Nach einem Abbruch ------------------------------------------------------
//
// Fehlt die Antwort auf einen Befehl, weiss nur der Server, ob er gewirkt hat.
// Der Bildschirm laedt deshalb still nach und entscheidet am Stand des Laufs
// (ADR-0058 Ziffer 5). Ausfuehren und Verwerfen sind dabei sicher zu
// wiederholen: ein zweites Mal trifft einen abgeschlossenen Lauf und wirkt
// nicht. Ruecknahme und neue Vorschau legen dagegen einen Lauf an; wiederholt
// stuende ein zweiter offener da, und die Antwort nennt keinen schon offenen.

/** Die Befehle, die nach einem Abbruch "Erneut versuchen" anbieten. */
export type RepeatableCommand = 'execute' | 'discard';

/**
 * Was der nachgeladene Stand ueber den abgebrochenen Befehl sagt: `arrived`
 * (er hat gewirkt), `open` (der Lauf steht weiter als Vorschau), `unknown`
 * (das Nachladen scheiterte), `closed` (anders abgeschlossen) oder `gone`
 * (der Lauf ist fuer mich nicht mehr sichtbar -- etwa nach einer
 * abgelehnten Freigabe, ADR-0058 Ziffer 6).
 */
export type AbortOutcome = 'arrived' | 'open' | 'unknown' | 'closed' | 'gone';

export function settleAfterAbort(command: RepeatableCommand, status: BatchStatus | 'gone' | undefined): AbortOutcome {
  if (status === undefined) return 'unknown';
  if (status === 'gone') return 'gone';
  if (status === (command === 'execute' ? 'completed' : 'discarded')) return 'arrived';
  return status === 'previewed' ? 'open' : 'closed';
}

/** Die Erfolgsmeldung, wenn erst das Nachladen zeigt, dass der Befehl gewirkt hat. */
export function arrivedSentence(command: RepeatableCommand): string {
  return command === 'execute'
    ? 'Dein erster Versuch hat den Massenlauf schon ausgeführt.'
    : 'Dein erster Versuch hat den Massenlauf schon verworfen. Es wurde nichts geändert.';
}

export function abortedMessage(command: RepeatableCommand, outcome: Exclude<AbortOutcome, 'arrived'>): PlainMessage {
  if (outcome === 'open') {
    return {
      headline: command === 'execute' ? 'Der Massenlauf ist nicht ausgeführt.' : 'Der Massenlauf ist nicht verworfen.',
      explanation: 'Die Antwort ist ausgeblieben, und der Lauf steht weiter als Vorschau da. Es wurde nichts geändert.',
      nextStep: 'Versuch es erneut.',
      retryable: true,
    };
  }
  if (outcome === 'unknown') {
    return {
      // Der Kopf sagt, was offen ist; die Erklaerung, warum (Netzsatz-Regel, DESIGN zu #717).
      headline: command === 'execute' ? 'Ob der Massenlauf ausgeführt wurde, ist offen.' : 'Ob der Massenlauf verworfen wurde, ist offen.',
      explanation: 'Die Antwort ist ausgeblieben. Ob dein Befehl angekommen ist, ließ sich nicht prüfen.',
      nextStep:
        command === 'execute'
          ? 'Prüfe deine Verbindung und versuch es erneut. Ein schon ausgeführter Lauf wird dabei nicht noch einmal ausgeführt.'
          : 'Prüfe deine Verbindung und versuch es erneut. Ein schon verworfener Lauf bleibt dabei verworfen.',
      retryable: true,
    };
  }
  if (outcome === 'gone') {
    return {
      headline: 'Dieser Massenlauf wartet nicht mehr auf dich.',
      explanation:
        'Die Antwort ist ausgeblieben, und der Lauf ist für dich nicht mehr zu sehen. Vermutlich hat dein erster Versuch gewirkt, oder jemand anderes hat ihn abgeschlossen.',
      nextStep: 'Du musst nichts weiter tun.',
      retryable: false,
    };
  }
  return {
    headline: 'Dieser Massenlauf ist inzwischen abgeschlossen.',
    explanation: 'Die Antwort auf deinen Befehl ist ausgeblieben, und der Lauf steht jetzt anders da.',
    nextStep: 'Darunter siehst du, wie er jetzt steht.',
    retryable: false,
  };
}

export const REVERSE_ABORTED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Antwort ist ausgeblieben.',
  explanation: 'Ob die Rücknahme vorbereitet wurde, ist offen. Geändert hat sich nichts: eine Rücknahme wirkt erst, wenn du sie bestätigst.',
  nextStep: 'Sieh unter „Massenpflege“ nach, ob dort eine neue Rücknahme steht. Steht dort keine, öffne diesen Lauf von dort aus und versuch es noch einmal.',
  retryable: false,
});

export const AGAIN_ABORTED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Antwort ist ausgeblieben.',
  explanation: 'Ob die neue Vorschau angelegt wurde, ist offen. Geändert hat sich nichts: eine Vorschau wirkt erst, wenn sie ausgeführt wird.',
  nextStep: 'Sieh unter „Massenpflege“ nach, ob dort eine neue Vorschau steht. Steht dort keine, öffne diesen Lauf von dort aus und versuch es noch einmal.',
  retryable: false,
});

const REASON_MESSAGES: Readonly<Record<string, PlainMessage>> = Object.freeze({
  classification_batch_outdated: OUTDATED_MESSAGE,
  classification_batch_expired: EXPIRED_MESSAGE,
  classification_batch_not_open: NOT_OPEN_MESSAGE,
  classification_batch_without_effect: WITHOUT_EFFECT_MESSAGE,
  classification_batch_second_person_required: SECOND_PERSON_MESSAGE,
  classification_batch_not_reversible: NOT_REVERSIBLE_MESSAGE,
  classification_batch_reversal_expired: REVERSAL_EXPIRED_MESSAGE,
  classification_batch_not_found: GONE_MESSAGE,
  taxonomy_term_not_active: TERM_NOT_ACTIVE_MESSAGE,
  taxonomy_term_not_replaced: NOT_REPLACED_MESSAGE,
  taxonomy_term_merge_target_unavailable: MERGE_TARGET_UNAVAILABLE_MESSAGE,
  taxonomy_term_groupwide: GROUPWIDE_MESSAGE,
});

/** Die Grundcodes, die diese Oberflaeche selbst erklaert -- die Probe haelt sie gegen die Route. */
export const EXPLAINED_REASON_CODES: readonly string[] = Object.freeze(Object.keys(REASON_MESSAGES));

export function messageForReason(reasonCode: string | undefined, facts: ResponseFacts): PlainMessage {
  return (reasonCode === undefined ? undefined : REASON_MESSAGES[reasonCode]) ?? plainMessageFor(reasonCode, facts);
}

// --- Worte fuer Zustand und Zahlen ------------------------------------------

function kontakte(n: number): string {
  return n === 1 ? 'einem Kontakt' : `${n} Kontakten`;
}

/**
 * Der Name eines Laufs als Vorgang, konkret statt "Vergeben: Spannung":
 * Liste, Brotkrume und Ueberschrift nennen dasselbe. Die Zahl ist die wirksame des Servers.
 */
export function laufName(batch: Pick<BatchSummary, 'operation' | 'termDisplayName' | 'effectiveCount' | 'isReversal'>): string {
  const n = batch.effectiveCount;
  const an = n === 1 ? '1 Kontakt' : `${n} Kontakte`;
  const bei = n === 1 ? '1 Kontakt' : `${n} Kontakten`;
  // Die Ruecknahme eines Umhaengens behaelt die Art merge_terms, vergibt aber das alte Stichwort wieder.
  if (batch.isReversal && batch.operation === 'merge_terms') return `Rücknahme: Stichwort „${batch.termDisplayName}“ wieder an ${an} vergeben`;
  const kern =
    batch.operation === 'assign'
      ? `Stichwort „${batch.termDisplayName}“ an ${an} vergeben`
      : batch.operation === 'remove'
        ? `Stichwort „${batch.termDisplayName}“ bei ${bei} entfernen`
        : `Stichwort „${batch.termDisplayName}“ bei ${bei} umhängen`;
  return batch.isReversal ? `Rücknahme: ${kern}` : kern;
}

/** Was bis zum Ende der Vorschau nicht geschieht, je Art; eine unbekannte Art bekommt den neutralen Satz. */
export function untilPreviewEndsSentence(batch: Pick<BatchSummary, 'operation' | 'isReversal'>): string {
  // Die Ruecknahme eines Umhaengens vergibt UND entfernt: dort der neutrale Satz, der beides zusagt.
  if (batch.operation === 'merge_terms') return batch.isReversal ? 'Bis dahin ändert sich an den Kontakten nichts.' : 'Bis dahin wird nichts umgehängt.';
  if (batch.operation === 'assign') return 'Bis dahin wird nichts vergeben.';
  if (batch.operation === 'remove') return 'Bis dahin wird nichts entfernt.';
  return 'Bis dahin ändert sich an den Kontakten nichts.';
}

/** Der Hauptsatz der Vorschau: nur Wirkung und Konflikte (Experience-Review P3). */
export function previewHeadline(
  batch: Pick<BatchDetail, 'operation' | 'termDisplayName' | 'effectiveCount' | 'isReversal'> & { readonly merge?: BatchMergeFacts | null },
): string {
  if (batch.effectiveCount === 0) {
    return `Dieser Lauf würde „${batch.termDisplayName}“ an keinem Kontakt ändern.`;
  }
  if (batch.operation === 'merge_terms') {
    const ziel = batch.merge == null ? 'seinen Nachfolger' : `„${batch.merge.targetDisplayName}“`;
    return batch.isReversal
      ? `Rückgängig machen vergibt „${batch.termDisplayName}“ wieder an ${kontakte(batch.effectiveCount)}.`
      : `„${batch.termDisplayName}“ endet bei ${kontakte(batch.effectiveCount)}; sie bekommen stattdessen ${ziel}.`;
  }
  if (batch.isReversal) {
    return batch.operation === 'remove'
      ? `Rückgängig machen entfernt „${batch.termDisplayName}“ wieder bei ${kontakte(batch.effectiveCount)}.`
      : `Rückgängig machen vergibt „${batch.termDisplayName}“ wieder an ${kontakte(batch.effectiveCount)}.`;
  }
  return batch.operation === 'assign'
    ? `„${batch.termDisplayName}“ wird an ${kontakte(batch.effectiveCount)} vergeben.`
    : `„${batch.termDisplayName}“ wird bei ${kontakte(batch.effectiveCount)} entfernt.`;
}

/** Ob der Lauf schon gewirkt hat: dann zeigt die Seite das Ergebnis in der Vergangenheit, keine Vorschau. */
export function hasRun(batch: Pick<BatchSummary, 'status'>): boolean {
  return batch.status === 'completed' || batch.status === 'reversed';
}

/** Der Hauptsatz eines Laufs, der schon gewirkt hat (DESIGN zu #572): was geschah, nicht was geschaehe. */
export function resultHeadline(
  batch: Pick<BatchDetail, 'operation' | 'termDisplayName' | 'effectiveCount' | 'isReversal'> & { readonly merge?: BatchMergeFacts | null },
): string {
  const n = kontakte(batch.effectiveCount);
  if (batch.operation === 'merge_terms') {
    const ziel = batch.merge == null ? 'seinen Nachfolger' : `„${batch.merge.targetDisplayName}“`;
    return batch.isReversal
      ? `„${batch.termDisplayName}“ wurde wieder an ${n} vergeben.`
      : `„${batch.termDisplayName}“ endete bei ${n}; sie haben stattdessen ${ziel} bekommen.`;
  }
  if (batch.isReversal) {
    return batch.operation === 'remove'
      ? `„${batch.termDisplayName}“ wurde wieder bei ${n} entfernt.`
      : `„${batch.termDisplayName}“ wurde wieder an ${n} vergeben.`;
  }
  return batch.operation === 'assign'
    ? `„${batch.termDisplayName}“ wurde an ${n} vergeben.`
    : `„${batch.termDisplayName}“ wurde bei ${n} entfernt.`;
}

/** Der Ort im Satz, wie die Seite heisst; ein Bildschirm darf ihn durch einen Link ersetzen. */
export const STICHWORTKONFLIKTE_ORT = '„Stichwortkonflikte“';

/**
 * Der Satz zu Kontakten mit zwei Stichworten einer Liste, die nur eines
 * vorsieht. Vor dem Lauf im Futur, danach in der Gegenwart (DESIGN #572);
 * der Ort heisst wie die Seite, auf die der Link daneben fuehrt (L14-01).
 */
export function conflictSentence(conflictCount: number, ausgefuehrt = false, meinLauf = true): string | undefined {
  if (conflictCount === 0) return undefined;
  const einer = conflictCount === 1;
  // Die Faelle gehoeren der Urheberin des Laufs (D1); bei einem fremden Lauf sagt der Satz das.
  // Der Ort steht in Anfuehrungszeichen; wo er verlinkt ist, ersetzt der Link sie (STICHWORTKONFLIKTE_ORT).
  const bei = meinLauf ? '' : ' bei der Person, die den Lauf angelegt hat,';
  if (ausgefuehrt) {
    return einer
      ? `Ein Kontakt trägt jetzt zwei Stichworte derselben Liste, obwohl sie nur eines vorsieht. Er steht${bei} unter ${STICHWORTKONFLIKTE_ORT}.`
      : `${conflictCount} Kontakte tragen jetzt zwei Stichworte derselben Liste, obwohl sie nur eines vorsieht. Sie stehen${bei} unter ${STICHWORTKONFLIKTE_ORT}.`;
  }
  return einer
    ? `Ein Kontakt trägt danach zwei Stichworte derselben Liste, obwohl sie nur eines vorsieht. Er steht dann${bei} unter ${STICHWORTKONFLIKTE_ORT}; der Lauf hält deshalb nicht an.`
    : `${conflictCount} Kontakte tragen danach zwei Stichworte derselben Liste, obwohl sie nur eines vorsieht. Sie stehen dann${bei} unter ${STICHWORTKONFLIKTE_ORT}; der Lauf hält deshalb nicht an.`;
}

/**
 * Die Wirkung eines merge-Laufs neben dem Hauptsatz -- sichtbar, nicht hinter
 * "Details": wie viele das Ziel neu bekommen und wie viele in einen Folgelauf
 * gehen. Jede Zahl kommt vom Server.
 */
export function mergeSentences(
  batch: Pick<BatchDetail, 'effectiveCount' | 'isReversal' | 'termDisplayName' | 'merge'>,
): readonly string[] {
  const merge = batch.merge;
  if (merge === null) return [];
  const saetze: string[] = [];
  const ziel = `„${merge.targetDisplayName}“`;
  if (batch.effectiveCount > 0) {
    if (batch.isReversal) {
      if (merge.targetCount > 0) saetze.push(`Bei ${merge.targetCount} davon endet ${ziel} wieder.`);
    } else if (merge.targetCount === batch.effectiveCount) {
      saetze.push(`Alle bekommen ${ziel} neu.`);
    } else {
      saetze.push(`${merge.targetCount} davon bekommen ${ziel} neu; die übrigen haben es schon.`);
    }
  }
  if (merge.remainingCount > 0) {
    saetze.push(`Beim Anlegen trugen ${merge.remainingCount} weitere „${batch.termDisplayName}“; sie folgen in einem nächsten Lauf.`);
  }
  return saetze;
}

/** Die Nebenzaehler fuer "Details" -- in Worten, nie als Tabelle. */
export function detailSentences(batch: BatchCounts & { readonly operation: BatchOperation; readonly isReversal: boolean }): readonly string[] {
  const saetze: string[] = [];
  if (batch.unchangedCount > 0) {
    saetze.push(
      batch.isReversal
        ? `${batch.unchangedCount} übersprungen: dort wurde inzwischen einzeln etwas geändert, und das bleibt so.`
        : batch.operation === 'assign'
          ? `${batch.unchangedCount} haben das Stichwort schon.`
          : batch.operation === 'merge_terms'
            ? `${batch.unchangedCount} tragen das ersetzte Stichwort nicht mehr.`
            : `${batch.unchangedCount} haben das Stichwort nicht.`,
    );
  }
  if (batch.notWritableCount > 0) {
    saetze.push(`${batch.notWritableCount} lassen sich nicht ändern (zusammengeführt oder anonymisiert).`);
  }
  if (batch.notFoundCount > 0) {
    saetze.push(`${batch.notFoundCount} sind für dich nicht vorhanden.`);
  }
  return saetze;
}

export function statusLabel(batch: Pick<BatchSummary, 'status' | 'previewCurrent' | 'requiresSecondPerson' | 'isMine'>): string {
  if (batch.status === 'completed') return 'Ausgeführt';
  if (batch.status === 'reversed') return 'Rückgängig gemacht';
  if (batch.status === 'discarded') return 'Verworfen';
  if (!batch.previewCurrent) return 'Abgelaufen';
  if (batch.requiresSecondPerson) return batch.isMine ? 'Wartet auf Freigabe' : 'Wartet auf deine Freigabe';
  return 'Vorschau';
}

/** Restzeit in Worten, gerechnet gegen die Uhr des Browsers -- nur Anzeige, entscheidend ist der Server. */
export function remainingLabel(until: string, now: Date = new Date()): string {
  const millis = Date.parse(until) - now.getTime();
  if (!Number.isFinite(millis) || millis <= 0) return 'abgelaufen';
  const tage = Math.floor(millis / 86_400_000);
  if (tage >= 2) return `noch ${tage} Tage`;
  if (tage === 1) return 'noch 1 Tag';
  const stunden = Math.max(1, Math.floor(millis / 3_600_000));
  return stunden === 1 ? 'noch 1 Stunde' : `noch ${stunden} Stunden`;
}

/**
 * Die Zahl der Auswahl -- ohne Grenze: eine Mengen-Grenze gibt es nur bei
 * technischem Schaden, und die Auswahl hat keinen. Was mit ihr geht, sagt jede
 * Aktion selbst; nur die Pflege bleibt bei MAXIMUM_BATCH_PARTIES (ADR-0058).
 */
export function selectionSummary(count: number): string {
  if (count === 0) return 'Kein Kontakt ausgewählt.';
  return count === 1 ? '1 Kontakt ausgewählt.' : `${count.toLocaleString('de-DE')} Kontakte ausgewählt.`;
}

/** Die Zahl ohne sichtbare Zeilen, weil der Abruf scheiterte: mit dem Grund, nie als blosse Zaehlung. */
export function selectionHeldNotice(count: number): string {
  const gewaehlt = count === 1 ? '1 Kontakt ausgewählt' : `${count.toLocaleString('de-DE')} Kontakte ausgewählt`;
  return `${gewaehlt} – die Liste konnte nicht geladen werden; die Auswahl bleibt erhalten.`;
}

/**
 * Steht in der Statusregion, wenn Kontakte die Auswahl verlassen haben, ohne
 * dass jemand sie abgewaehlt hat -- kein stiller Verlust. `ergebnis`: ein
 * anderes Ergebnis hat die ganze Auswahl aufgehoben; `zeilen`: das neu geladene
 * Ergebnis zeigt einige Ausgewaehlte nicht mehr.
 */
export function selectionDiscardedNotice(count: number, grund: 'ergebnis' | 'zeilen'): string {
  const kontakte = count === 1 ? '1 Kontakt' : `${count} Kontakte`;
  return grund === 'ergebnis'
    ? `Die Auswahl aus dem vorigen Ergebnis (${kontakte}) ist aufgehoben.`
    : `${kontakte} ${count === 1 ? 'steht' : 'stehen'} nicht mehr in der Liste und ${count === 1 ? 'ist' : 'sind'} nicht mehr ausgewählt.`;
}

/**
 * Warum "Stichwort pflegen" ueber 250 nicht geht und was stattdessen geht
 * (ADR-0058: ein Lauf, vier Augen, pruefbar).
 */
export function maintenanceLimitSentence(count: number): string {
  return `Ein Massenlauf fasst höchstens ${MAXIMUM_BATCH_PARTIES} Kontakte, damit jede Änderung prüfbar bleibt. Du hast ${count.toLocaleString('de-DE')} ausgewählt. Nimm einige heraus, um ein Stichwort zu pflegen. Exportieren und „Zur Sammlung …“ gehen mit allen.`;
}

/** Ob die Auswahl fuer einen Massenlauf zu gross ist. */
export function exceedsBatch(count: number): boolean {
  return count > MAXIMUM_BATCH_PARTIES;
}

export function massenlaufHref(batchId: string): string {
  return `/massenpflege/${encodeURIComponent(batchId)}`;
}

// --- Die Aufrufe ---------------------------------------------------------------

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

const OPERATIONS: readonly string[] = ['assign', 'remove', 'merge_terms'];
const STATUSES: readonly string[] = ['previewed', 'completed', 'reversed', 'discarded'];
const OUTCOMES: readonly string[] = ['effective', 'unchanged', 'not_writable', 'not_found'];

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function summaryOf(value: unknown): BatchSummary | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  const optionalText = (key: string): string | null | undefined =>
    row[key] === null ? null : typeof row[key] === 'string' ? (row[key] as string) : undefined;
  const completedAt = optionalText('completedAt');
  const reversibleUntil = optionalText('reversibleUntil');
  if (
    typeof row.classificationChangeBatchId !== 'string' ||
    typeof row.operation !== 'string' || !OPERATIONS.includes(row.operation) ||
    typeof row.status !== 'string' || !STATUSES.includes(row.status) ||
    typeof row.taxonomyTermId !== 'string' ||
    typeof row.termDisplayName !== 'string' ||
    typeof row.isMine !== 'boolean' ||
    typeof row.isReversal !== 'boolean' ||
    typeof row.requiresSecondPerson !== 'boolean' ||
    typeof row.createdAt !== 'string' ||
    typeof row.previewExpiresAt !== 'string' ||
    typeof row.previewCurrent !== 'boolean' ||
    typeof row.canReverse !== 'boolean' ||
    completedAt === undefined || reversibleUntil === undefined ||
    !isCount(row.effectiveCount) || !isCount(row.unchangedCount) || !isCount(row.notWritableCount) ||
    !isCount(row.notFoundCount) || !isCount(row.conflictCount)
  ) {
    return undefined;
  }
  return {
    classificationChangeBatchId: row.classificationChangeBatchId,
    operation: row.operation as BatchOperation,
    status: row.status as BatchStatus,
    taxonomyTermId: row.taxonomyTermId,
    termDisplayName: row.termDisplayName,
    isMine: row.isMine,
    isReversal: row.isReversal,
    requiresSecondPerson: row.requiresSecondPerson,
    createdAt: row.createdAt,
    previewExpiresAt: row.previewExpiresAt,
    previewCurrent: row.previewCurrent,
    completedAt,
    reversibleUntil,
    canReverse: row.canReverse,
    effectiveCount: row.effectiveCount,
    unchangedCount: row.unchangedCount,
    notWritableCount: row.notWritableCount,
    notFoundCount: row.notFoundCount,
    conflictCount: row.conflictCount,
  };
}

function detailOf(value: unknown): BatchDetail | undefined {
  const summary = summaryOf(value);
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    summary === undefined ||
    !isCount(row.requestedCount) ||
    typeof row.outdated !== 'boolean' ||
    typeof row.canExecute !== 'boolean' ||
    typeof row.canDiscard !== 'boolean' ||
    !Array.isArray(row.contacts) ||
    !isCount(row.additionalContactCount)
  ) {
    return undefined;
  }
  const contacts: BatchContact[] = [];
  for (const entry of row.contacts) {
    const contact = (entry ?? {}) as Record<string, unknown>;
    if (
      typeof contact.partyId !== 'string' ||
      typeof contact.displayName !== 'string' ||
      !(contact.outcome === null || (typeof contact.outcome === 'string' && OUTCOMES.includes(contact.outcome)))
    ) {
      return undefined;
    }
    contacts.push({ partyId: contact.partyId, displayName: contact.displayName, outcome: contact.outcome as BatchItemOutcome | null });
  }
  // Ohne Ziel (ein frueher angelegter Gegenlauf) bleibt merge leer; der Hauptsatz spricht dann vom Nachfolger.
  let merge: BatchMergeFacts | null = null;
  if (summary.operation === 'merge_terms' && row.targetDisplayName !== undefined) {
    if (typeof row.targetDisplayName !== 'string' || !isCount(row.targetCount) || !isCount(row.remainingCount)) {
      return undefined;
    }
    merge = { targetDisplayName: row.targetDisplayName, targetCount: row.targetCount, remainingCount: row.remainingCount };
  }
  return {
    ...summary,
    requestedCount: row.requestedCount,
    outdated: row.outdated,
    canExecute: row.canExecute,
    canDiscard: row.canDiscard,
    contacts,
    additionalContactCount: row.additionalContactCount,
    merge,
  };
}

const UNVOLLSTAENDIG: PlainMessage = Object.freeze({
  headline: 'Die Massenpflege lässt sich nicht anzeigen.',
  explanation: 'LIAISON hat geantwortet, aber die Antwort war unvollständig.',
  nextStep: 'Lade die Seite neu. Bleibt es dabei, wende dich an die LIAISON-Administration.',
  retryable: true,
});

interface CallOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}

export async function fetchBatches(options: CallOptions): Promise<ListResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/classification-batches`, { headers: { authorization: `Bearer ${options.accessToken}` } });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    return { outcome: 'rejected', message: messageForReason(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const own = Array.isArray(body.ownBatches) ? body.ownBatches.map(summaryOf) : [undefined];
  const awaiting = Array.isArray(body.awaitingApproval) ? body.awaitingApproval.map(summaryOf) : [undefined];
  if (own.includes(undefined) || awaiting.includes(undefined)) {
    return { outcome: 'rejected', message: UNVOLLSTAENDIG };
  }
  return { outcome: 'loaded', ownBatches: own as BatchSummary[], awaitingApproval: awaiting as BatchSummary[] };
}

export async function fetchBatch(options: CallOptions & { readonly batchId: string }): Promise<DetailResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/classification-batches/${encodeURIComponent(options.batchId)}`, {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'gone' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: messageForReason(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const batch = detailOf(payload);
  return batch === undefined ? { outcome: 'rejected', message: UNVOLLSTAENDIG } : { outcome: 'loaded', batch };
}

async function command(options: CallOptions, path: string, body: unknown): Promise<CommandResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}${path}`, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    const reasonCode = reasonCodeOf(payload);
    return { outcome: 'rejected', ...(reasonCode === undefined ? {} : { reasonCode }), message: messageForReason(reasonCode, factsOf(response, payload)) };
  }
  const antwort = (payload ?? {}) as Record<string, unknown>;
  const id = antwort.classificationChangeBatchId;
  // Nur die Ausfuehrung meldet angelegte Datenqualitaetsfaelle (ADR-0059);
  // alle anderen Befehle zaehlen 0.
  const faelle = typeof antwort.dataQualityIssueCount === 'number' && Number.isInteger(antwort.dataQualityIssueCount) ? antwort.dataQualityIssueCount : 0;
  return typeof id === 'string'
    ? { outcome: 'applied', classificationChangeBatchId: id, dataQualityIssueCount: faelle }
    : { outcome: 'rejected', message: UNVOLLSTAENDIG };
}

/** Legt den Massenlauf an -- das IST die Vorschau (ADR-0058 Ziffer 2). */
export function createBatch(
  options: CallOptions & { readonly operation: BatchOperation; readonly taxonomyTermId: string; readonly partyIds: readonly string[] },
): Promise<CommandResult> {
  return command(options, '/classification-batches', {
    operation: options.operation,
    taxonomyTermId: options.taxonomyTermId,
    partyIds: [...options.partyIds],
  });
}

/**
 * Legt einen merge-Lauf fuer ein ersetztes Stichwort an -- ohne Kontaktliste,
 * den Objektsatz rechnet der Server (K3). Ohne offene Zuordnung entsteht kein
 * Lauf; der Satz dazu spricht vom Stichwort, nicht von einer Auswahl.
 */
export async function createMergeBatch(options: CallOptions & { readonly taxonomyTermId: string }): Promise<CommandResult> {
  const result = await command(options, '/classification-batches', { operation: 'merge_terms', taxonomyTermId: options.taxonomyTermId });
  return result.outcome === 'rejected' && result.reasonCode === 'classification_batch_without_effect'
    ? { ...result, message: MERGE_WITHOUT_EFFECT_MESSAGE }
    : result;
}

export function executeBatch(options: CallOptions & { readonly batchId: string }): Promise<CommandResult> {
  return command(options, `/classification-batches/${encodeURIComponent(options.batchId)}/execute`, {});
}

export function discardBatch(options: CallOptions & { readonly batchId: string }): Promise<CommandResult> {
  return command(options, `/classification-batches/${encodeURIComponent(options.batchId)}/discard`, {});
}

export function reverseBatch(options: CallOptions & { readonly batchId: string }): Promise<CommandResult> {
  return command(options, `/classification-batches/${encodeURIComponent(options.batchId)}/reverse`, {});
}

// --- Fluechtiger Zustand der Auswahl -----------------------------------------
//
// Die Auswahl lebt NUR im Arbeitsspeicher der Anwendung -- nie in der Adresse,
// nie im Browser-Speicher: Kontaktkennungen und Namen sind Personendaten, und
// dieselbe Regel gilt fuer den Suchbegriff (parties.ts, ADR-0049). Sie endet mit
// der Abmeldung (forgetBatchSelection in main.tsx).
//
// Sie gehoert zu dem Ergebnis, in dem sie entstand: der Rueckweg in dieselbe
// Suche findet sie wieder, jede andere Suche, jeder andere Filter und die andere
// Adresse heben sie auf und sagen das an. Sonst zaehlte der Satz Kontakte mit,
// deren Haekchen niemand sieht -- und eine Aktion auf "die Auswahl" traefe sie
// ungesehen. Sammeln ueber mehrere Suchen ist ein eigener Begriff (F3).

export interface SelectedParty {
  readonly partyId: string;
  readonly displayName: string;
}

export interface BatchRequest {
  readonly operation: BatchOperation;
  readonly taxonomyTermId: string;
  readonly partyIds: readonly string[];
}

let rememberedSelection: { readonly origin: string; readonly parties: readonly SelectedParty[] } | undefined;
const rememberedRequests = new Map<string, BatchRequest>();

/**
 * `origin` benennt das Ergebnis, etwa Adresse, Begriff und Filter -- verglichen
 * wird nur auf Gleichheit. `discarded` zaehlt eine Auswahl aus einem anderen
 * Ergebnis; aufheben muss sie der Aufrufer, damit dieses Lesen ohne Wirkung bleibt.
 */
export function recallSelection(origin: string): { readonly parties: readonly SelectedParty[]; readonly discarded: number } {
  if (rememberedSelection === undefined) return { parties: [], discarded: 0 };
  return rememberedSelection.origin === origin
    ? { parties: rememberedSelection.parties, discarded: 0 }
    : { parties: [], discarded: rememberedSelection.parties.length };
}

export function rememberSelection(origin: string, selection: readonly SelectedParty[]): void {
  rememberedSelection = { origin, parties: [...selection] };
}

/** Merkt sich, WAS ein angelegter Lauf wollte -- fuer "Gleiche Auswahl erneut vorlegen". */
export function rememberBatchRequest(batchId: string, request: BatchRequest): void {
  rememberedRequests.set(batchId, { ...request, partyIds: [...request.partyIds] });
}

export function recallBatchRequest(batchId: string): BatchRequest | undefined {
  return rememberedRequests.get(batchId);
}

export function forgetBatchSelection(): void {
  rememberedSelection = undefined;
  rememberedRequests.clear();
}

/** Fuegt einen Kontakt hinzu oder nimmt ihn heraus. */
export function toggleSelected(selection: readonly SelectedParty[], party: SelectedParty): readonly SelectedParty[] {
  if (selection.some((entry) => entry.partyId === party.partyId)) {
    return selection.filter((entry) => entry.partyId !== party.partyId);
  }
  return [...selection, party];
}

/** "Alle auf dieser Seite": fuegt hinzu, was fehlt -- oder nimmt alle heraus, wenn schon alle drin sind. */
export function togglePage(selection: readonly SelectedParty[], page: readonly SelectedParty[]): readonly SelectedParty[] {
  const chosen = new Set(selection.map((entry) => entry.partyId));
  if (page.length > 0 && page.every((party) => chosen.has(party.partyId))) {
    const onPage = new Set(page.map((party) => party.partyId));
    return selection.filter((entry) => !onPage.has(entry.partyId));
  }
  const next = [...selection];
  for (const party of page) {
    if (!chosen.has(party.partyId)) next.push(party);
  }
  return next;
}
